import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import classNames from "classnames";
import style from "./EnhancedSearchResults.module.scss";
import { highlightResult } from "../../../utils/resultHighlight";
import { updateState, selectDitaPath } from "../../../features/counter/counterSlice";

const SearchResultItem = ({
  selectedTab,
  searchTerm,
  searchResult,
  isAppMode = false,
  isWebModernisationEnabled,
  showBorder = true,
}) => {
  const dispatch = useDispatch();
  const currentDitaPath = useSelector(selectDitaPath);
  let iconClassName = searchResult.guideline.replace(/ /g, "").toLowerCase();
  const formattedTitle = searchResult?.title?.replace(/<[^>]*>/g, "") ?? "";
  const [showFiveSubTopics, setShowFiveSubTopics] = useState(false);
  const visibleSubTopics = showFiveSubTopics
    ? searchResult.subTopics?.slice(0, 5)
    : searchResult.subTopics?.slice(0, 2);

  const clickShowHideButton = () => {
    setShowFiveSubTopics(!showFiveSubTopics);
  };
  const selectedTabURL = selectedTab === 1 ? searchResult.url : searchResult.topicUrl;
  const handleTopicClick = () => {
    const params = new URLSearchParams(new URL(selectedTabURL, window.location.origin).search);
    const sectionId = params.get("sectionId") || "";
    const targetGuideline = (params.get("guidelinePage") || "").replace(/ /g, "_");
    const normalizedCurrent = currentDitaPath.replace(/\//g, "");
    const isCrossGuideline =
      targetGuideline && normalizedCurrent && targetGuideline !== normalizedCurrent;
    if (isCrossGuideline) {
      // Clear stale map and value so Counter's PrepareToView cannot find a topic
      // in the old guideline's map and navigate back when the location changes.
      // Do NOT set hashRedirect:true here — it would trigger the auth effect with
      // the old ditaPath, re-loading the old map and repeating the revert.
      dispatch(updateState({ map: "", value: [] }));
    } else {
      dispatch(updateState({ XRefPath: sectionId, hashRedirect: true }));
    }
  };

  const handleSubTopicClick = () => {
    const params = new URLSearchParams(new URL(searchResult.url, window.location.origin).search);
    const sectionId = params.get("sectionId") || "";
    dispatch(updateState({ XRefPath: sectionId }));
  };

  return (
    <li className={classNames(style.resultChip, { [style.border]: showBorder })}>
      <div className={classNames(style.resultTitleSection)}>
        <Link
          to={selectedTabURL}
          className={classNames(style.resultTitle, "semibold", "size-16 ")}
          title={formattedTitle}
          onClick={handleTopicClick}
          dangerouslySetInnerHTML={{
            __html: highlightResult(searchResult.title, searchTerm),
          }}
        ></Link>
        <div className={classNames(style.guidelineChip, style.desktopOnly)}>
          <div className={classNames(style[iconClassName], style.guidelineIcon)}></div>
          <div className={classNames("size-12", style.guidelineText)}>{searchResult.guideline}</div>
        </div>
      </div>
      <div
        className={classNames(
          "regular",
          { "size-16": isAppMode !== true, "size-14": isAppMode === true },
          style.resultText,
        )}
        dangerouslySetInnerHTML={{
          __html: highlightResult(
            !searchResult.summary && searchResult.subTopics?.length > 0
              ? searchResult.subTopics[0].topicTitle
              : searchResult.summary,
            searchTerm,
          ),
        }}
      ></div>
      {isWebModernisationEnabled &&
        Array.isArray(searchResult?.subTopics) &&
        searchResult.subTopics.length > 0 && (
          <div className={style.subTopicsWrapper}>
            {visibleSubTopics.map((subTopic) => (
              <div
                key={subTopic.url}
                className={classNames(
                  "regular",
                  { "size-16": !isAppMode, "size-14": isAppMode },
                  style.subTopicText,
                )}
              >
                <Link
                  to={subTopic.url}
                  className={style.subTopicLink}
                  onClick={handleSubTopicClick}
                  dangerouslySetInnerHTML={{
                    __html: highlightResult(subTopic.topicTitle, searchTerm),
                  }}
                ></Link>
              </div>
            ))}

            {searchResult.subTopics.length > 2 && (
              <div className={style.searchWithinTitle}>
                <button
                  className={style.toggleButton}
                  onClick={clickShowHideButton}
                  data-testid="search-within-result"
                >
                  {showFiveSubTopics ? (
                    <div>
                      <span className={style.subheading}>Hide subtopics</span>
                      <i className="fa banner-head-topic fa-angle-up"></i>
                    </div>
                  ) : (
                    <div>
                      <span className={style.subheading}>View more subtopics</span>
                      <i className="fa banner-head-topic fa-angle-down"></i>
                    </div>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      <div className={classNames(style.guidelineChip, style.mobileOnly)}>
        <div className={classNames(style[iconClassName], style.guidelineIcon)}></div>
        <div className={classNames("size-12", style.guidelineText)}>{searchResult.guideline}</div>
      </div>
    </li>
  );
};

SearchResultItem.propTypes = {
  searchTerm: PropTypes.string,
  isAppMode: PropTypes.bool,
  isWebModernisationEnabled: PropTypes.bool,
  selectedTab: PropTypes.number,
  searchResult: PropTypes.shape({
    guideline: PropTypes.string,
    title: PropTypes.string,
    summary: PropTypes.string,
    url: PropTypes.string,
    topicUrl: PropTypes.string,
  }),
};

export default SearchResultItem;
